/**
 * The scroll footer both cleaning tabs share (CAP-412).
 *
 * **"Load more" is a real button, not decoration behind the observer.**
 * Auto-loading alone strands keyboard users - nothing focusable advances the
 * list - and screen-reader users, for whom the list simply never ends. The
 * observer clicks this button, so there is exactly one path into loading and
 * the button can never be out of step with it.
 */

import { useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'

export interface InfiniteListFooterProps {
  hasMore: boolean
  loading: boolean
  onLoadMore: () => void
  /** Rows on screen and rows matching, for the announcement. */
  shown: number
  total: number
  noun: string
}

export function InfiniteListFooter({
  hasMore,
  loading,
  onLoadMore,
  shown,
  total,
  noun,
}: InfiniteListFooterProps) {
  const sentinel = useRef<HTMLDivElement | null>(null)
  const loadMore = useRef(onLoadMore)

  // Kept current in an effect rather than assigned during render: writing a
  // ref while rendering is a side effect in the render phase, which React may
  // run more than once per commit.
  useEffect(() => {
    loadMore.current = onLoadMore
  }, [onLoadMore])

  useEffect(() => {
    const node = sentinel.current
    if (!node || !hasMore || loading) return
    if (typeof IntersectionObserver === 'undefined') return

    // `rootMargin` pulls the trigger 400px above the fold so the next batch is
    // usually already there by the time the reader reaches it. An infinite
    // scroll that only fires once you HIT the bottom shows a gap every time.
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) loadMore.current()
        }
      },
      { rootMargin: '400px 0px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, loading])

  return (
    <>
      <div className="flex flex-wrap items-center justify-center gap-3 pt-4 text-sm text-textFaint">
        {hasMore && !loading && (
          <Button variant="outline" size="sm" onClick={onLoadMore}>
            Load more
          </Button>
        )}
        {loading && (
          <span className="inline-flex items-center gap-2 text-textMuted">
            <Spinner />
            Loading
          </span>
        )}
        {!hasMore && total > 0 && <span className="tabular-nums">End of list</span>}
      </div>
      <div ref={sentinel} aria-hidden="true" />
      {/* Announced rather than shown: with no pager there is no visible
          "page 2 of 12" for a screen reader to land on, so each batch says how
          far the list has got and when it has ended. */}
      <p className="sr-only" role="status" aria-live="polite">
        {total === 0
          ? `No ${noun} match these filters.`
          : `Showing ${shown} of ${total} ${noun}.${hasMore ? '' : ' End of list.'}`}
      </p>
    </>
  )
}

export interface LoadEarlierButtonProps {
  hasMore: boolean
  loading: boolean
  onLoadEarlier: () => void
  /** How far back one press reaches. */
  days: number
  /** Cleanings still behind the button. */
  earlierCount: number
}

/**
 * The upward half of the list: a button, and deliberately nothing else.
 *
 * This used to be an infinite scroll as well (CAP-526), with an observer that
 * loaded a batch whenever the reader reached the top. That made the top
 * unreachable - every arrival at it prepended another batch and pushed it away
 * again, so the tabs and page header above the list could not be got back to.
 * Scrolling down never has that problem, which is why the footer keeps its
 * observer and this does not.
 *
 * Loading only on a press also means nothing moves under the reader: the rows
 * land directly below the button, in view, and the button stays where it was
 * for the next press.
 */
export function LoadEarlierButton({
  hasMore,
  loading,
  onLoadEarlier,
  days,
  earlierCount,
}: LoadEarlierButtonProps) {
  if (!hasMore && !loading) return null

  return (
    <div className="flex flex-wrap items-center justify-center gap-3 pb-4 text-sm text-textFaint">
      {loading ? (
        <span className="inline-flex items-center gap-2 text-textMuted">
          <Spinner />
          Loading earlier cleanings
        </span>
      ) : (
        <>
          <Button variant="outline" size="sm" onClick={onLoadEarlier}>
            Load previous {days} days
          </Button>
          {/* Worth saying before anything loads: it is the one number that
              tells the operator whether going back is worth doing at all. */}
          <span className="tabular-nums">
            {earlierCount} earlier {earlierCount === 1 ? 'cleaning' : 'cleanings'}
          </span>
        </>
      )}
    </div>
  )
}
